/**
 * 3D Room Visualizer — placing staged props in a room.
 *
 * A floor prop stands in the room group at its staged position and yaw. A hung
 * or wall prop is built in its wall's canonical coordinates and put INTO that
 * wall's group, so it fades, locks and clears with the wall (looking in from
 * outside, a rake on a faded wall fades with it). A staged SlatWall accessory
 * (one staging hung itself) goes on its wall the same way, from its STL.
 *
 * A prop staged for one of Prop Studio's models (`StagedPropView.model`) is
 * drawn as that model's PREVIEW — its .glb, placed in the prop's frame by the
 * same arithmetic Prop Studio draws with (placement.ts) — once the file
 * arrives, the procedural stand-in hidden under it. The stand-in stays in the
 * group: the room export sends IT (standInsForExport), never the preview,
 * since the renderer swaps the stand-in for its own copy of the model.
 */

import * as THREE from 'three';
import { Placement, PropDef } from '../../../calculations/staging/kits';
import { SlatWallAccessoryKind } from '../../../data/slatwallAccessories';
import { applyFaceTransformToGroup } from './extrudedBoardBuilder';
import { placeAccessory } from './accessoryMeshes';
import { buildCeilingProp, buildFloorProp, buildHungProp } from './propBuilder';
import { WallFace } from './types';
import { copyOffsetsFeet, placeInThree } from '../../props/placement';
import type { PropModelView } from '../../props/variants';

/** The photoreal model a staged prop is drawn as: the model with its parts (a toolbox on the bench). */
export interface PropPreview { model: PropModelView; parts: PropModelView[] }

/** A staged prop as the viewer draws it: the kit's definition plus where it goes. */
export interface StagedPropView {
  id: string;
  /** The prop (absent for a staged accessory). */
  def?: PropDef;
  /** `yard`: outside the house, built like a floor prop (its room coordinates run past the walls) in the `staging-yard` group — for the render only, hidden in the view. */
  kind: 'floor' | 'slatwall' | 'wall' | 'accessory' | 'ceiling' | 'yard';
  /** How it was placed (staging/catalogue.ts): exported, so the renderer anchors the model to suit. */
  place?: Placement;
  /** Which of the prop's photoreal models it was staged for (the library key): exported, so the render shows that one. */
  variant?: string;
  /** That model's preview, when the library can serve it: drawn in the stand-in's place. */
  model?: PropPreview;
  /**
   * Where that model's box is centred in the prop's frame (feet: along, into
   * the room) when it is off the origin — a floor model standing off it, a
   * held model hung off to one side of its hook or out from the wall. The
   * stand-in is drawn there, where the room was kept clear and the model is;
   * the prop's origin (and a held prop's `hold`) stays where it was.
   */
  bodyOffset?: [number, number];
  /** Floor and ceiling props: room coordinates and yaw (a ceiling prop's `y` is the height it is held at). */
  x: number;
  z: number;
  rotation: number;
  /** Hung and wall props: the wall, x along it (surface coordinates), y up — where the prop is held. */
  face?: WallFace;
  y?: number;
  /** Hung props: how far out from the wall it is held (feet), from its accessory. */
  holdZ?: number;
  /** Staged accessories: what it is; `x`, `y` are its bottom-left on the wall. */
  accessoryKind?: SlatWallAccessoryKind;
}

/**
 * Add a room's props. Returns the group the floor props went into (hung ones
 * are inside their wall groups); the caller disposes it with the room.
 * `slatZ` is how far the SlatWall stands proud of the wall plane, for the
 * staged accessories; `onReady` is called as each accessory's file arrives,
 * and as each prop's preview does.
 */
export function addStagedProps(
  root: THREE.Object3D,
  walls: Partial<Record<WallFace, THREE.Group | null>>,
  props: StagedPropView[],
  roomLength: number,
  roomWidth: number,
  slatZ = 0,
  onReady?: () => void,
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'staging';
  // The yard's props (trees, shrubs) in a group of their own: the viewer hides it, the export shows it.
  let yardGroup: THREE.Group | null = null;
  for (const p of props) {
    if (p.kind === 'floor' || p.kind === 'yard') {
      if (!p.def) continue;
      const g = buildFloorProp(p.def);
      g.position.set(p.x, 0, p.z);
      g.rotation.y = p.rotation;
      g.userData.stagedPropId = p.id;
      if (p.place) g.userData.placement = p.place;
      if (p.variant) g.userData.variant = p.variant;
      // The stand-in where the model's box is, when that is off the origin (the group's origin stays the prop's: the renderer places the model from it).
      if (p.bodyOffset) for (const child of g.children) { child.position.x += p.bodyOffset[0]; child.position.z += p.bodyOffset[1]; }
      // The prop's frame is the group's own: standing on the floor at the origin, facing +z.
      if (p.model) showPreview(g, g, p.model, onReady);
      if (p.kind === 'yard') {
        if (!yardGroup) { yardGroup = new THREE.Group(); yardGroup.name = 'staging-yard'; group.add(yardGroup); }
        yardGroup.add(g);
      } else group.add(g);
      continue;
    }
    if (p.kind === 'ceiling') {
      if (!p.def || p.y === undefined) continue;
      // Hung from the ceiling: the group's origin is the point it is held at,
      // and its `hold` (for the renderer, as a hung prop's) is that point.
      const g = buildCeilingProp(p.def);
      g.position.set(p.x, p.y, p.z);
      g.rotation.y = p.rotation;
      const hold = new THREE.Object3D();
      hold.name = 'hold';
      g.add(hold);
      g.userData.stagedPropId = p.id;
      g.userData.held = true;
      g.userData.placement = 'ceiling';
      if (p.variant) g.userData.variant = p.variant;
      if (p.model) showPreview(g, hold, p.model, onReady);
      group.add(g);
      continue;
    }
    if (!p.face || p.y === undefined) continue;
    if (p.kind === 'accessory') {
      if (!p.accessoryKind) continue;
      placeAccessory(walls[p.face] ?? group, { id: p.id, staged: true, kind: p.accessoryKind, face: p.face, x: p.x, y: p.y, z: slatZ }, roomLength, roomWidth, onReady);
      continue;
    }
    if (!p.def) continue;
    const g = buildHungProp(p.def, p.x, p.y, p.holdZ);
    // The stand-in where the model's box is, when that is off the hold point (the hold below stays the prop's).
    if (p.bodyOffset) for (const child of g.children) { child.position.x += p.bodyOffset[0]; child.position.z += p.bodyOffset[1]; }
    // The face transform for the left and right walls rewrites geometry
    // vertices and leaves mesh positions alone (the board builders bake theirs
    // in), so bake each part's position and rotation into its geometry first.
    bakeTransforms(g);
    applyFaceTransformToGroup(g, p.face, roomLength, roomWidth);
    // Where the prop is HELD — the hook's prong, the shelf's top, the bar —
    // as a child the exporter writes out: the Blender renderer places a
    // photoreal model from it (scripts/props/README.md, "Held props"). In
    // the group's own frame, facing the room: for the front and back walls
    // the group carries the face transform, for the left and right walls the
    // geometry does, so the point is mapped by hand there.
    const hold = holdMarker(p.face, p.x, p.y, p.holdZ ?? p.def.holdZ ?? BUILDER_HOLD_Z, roomLength, roomWidth);
    g.add(hold);
    g.userData.stagedPropId = p.id;
    g.userData.held = true; // exported as an extra: the renderer expects the hold, and says so when it is missing
    if (p.place) g.userData.placement = p.place;
    if (p.variant) g.userData.variant = p.variant;
    // A held prop's frame has its origin at the hold point, facing the room: the hold marker is exactly that.
    if (p.model) showPreview(g, hold, p.model, onReady);
    const wall = walls[p.face];
    (wall ?? group).add(g);
  }
  root.add(group);
  return group;
}

/** How far out from the wall the hung builders draw their props (propBuilder.ts). */
const BUILDER_HOLD_Z = 0.3;

/**
 * An empty at a wall point (x along the wall, y up, holdZ out of it), turned
 * to face the room, in the frame of a prop group that has had the face
 * transform applied. Named `hold`: the room export carries it to the renderer.
 */
function holdMarker(face: WallFace, x: number, y: number, holdZ: number, roomLength: number, roomWidth: number): THREE.Object3D {
  const hold = new THREE.Object3D();
  hold.name = 'hold';
  switch (face) {
    case 'front':
    case 'back':
      hold.position.set(x, y, holdZ); // the group's transform takes it round
      break;
    case 'left':
      hold.position.set(holdZ, y, x);
      hold.rotation.y = Math.PI / 2; // +z → +x: into the room from the left wall
      break;
    case 'right':
      hold.position.set(roomLength - holdZ, y, roomWidth - x);
      hold.rotation.y = -Math.PI / 2;
      break;
  }
  return hold;
}

/** Fold every mesh's position / rotation / scale into its geometry, leaving the mesh at the origin. */
export function bakeTransforms(root: THREE.Object3D): void {
  root.updateMatrixWorld(true);
  root.traverse((c) => {
    const mesh = c as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.updateMatrix();
    mesh.geometry.applyMatrix4(mesh.matrix);
    mesh.position.set(0, 0, 0);
    mesh.rotation.set(0, 0, 0);
    mesh.scale.set(1, 1, 1);
    mesh.updateMatrix();
  });
}

// ---------------------------------------------------------------- previews

/**
 * Each preview file parsed once per page, whatever the number of copies and
 * rebuilds: a prop staged twice, or a room rebuilt after every edit, clones
 * the parsed scene (its geometry shared, its materials each copy's own, so a
 * wall's fade touches one copy only). The loader is an ES module the test
 * runner does not transform, so it is imported on demand, as the exporter is.
 */
const previewFiles = new Map<string, Promise<THREE.Group>>();

function loadPreviewFile(url: string): Promise<THREE.Group> {
  let pending = previewFiles.get(url);
  if (!pending) {
    pending = import('three/examples/jsm/loaders/GLTFLoader.js')
      .then(({ GLTFLoader }) => new GLTFLoader().loadAsync(url))
      .then((gltf) => gltf.scene);
    previewFiles.set(url, pending);
    pending.catch(() => previewFiles.delete(url));
  }
  return pending;
}

/**
 * A preview's material, lightened for the designer: its colour (and colour
 * picture) only — the normal, roughness, metalness, occlusion and emissive
 * pictures a BlenderKit model carries (up to thirty on one vanity) are for
 * the render, and each is another texture to decode, upload and sample.
 */
function lightMaterial(m: THREE.Material): THREE.Material {
  const src = m as THREE.MeshStandardMaterial;
  if (!src.isMeshStandardMaterial) return m.clone();
  const out = new THREE.MeshStandardMaterial({
    color: src.color, map: src.map ?? null, roughness: Math.max(0.35, src.roughness), metalness: Math.min(0.5, src.metalness),
    transparent: src.transparent, opacity: src.opacity, side: src.side, alphaTest: src.alphaTest,
  });
  out.name = src.name;
  return out;
}

/**
 * A copy of a parsed preview to draw: geometry shared, materials its own and
 * light (above), taking shadows but casting none — a shadow map is another
 * full pass over every triangle for every lamp, and these are the heaviest
 * meshes in the room by far.
 */
function previewInstance(file: THREE.Group): THREE.Group {
  const copy = file.clone(true);
  copy.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.material = Array.isArray(mesh.material) ? mesh.material.map(lightMaterial) : lightMaterial(mesh.material);
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.userData.propPreviewMesh = true; // disposal frees the materials, never the shared geometry
  });
  return copy;
}

/** Is this object still in a scene? A preview that arrives for a room already torn down is thrown away. */
function inScene(o: THREE.Object3D): boolean {
  let top: THREE.Object3D = o;
  while (top.parent) top = top.parent;
  return (top as THREE.Scene).isScene === true;
}

/**
 * Draw a prop's photoreal preview in its stand-in's place. `frame` is the
 * prop's frame as Prop Studio defines it (placement.ts): a floor prop's own
 * group, or a held prop's `hold` marker (origin at the hold point, facing the
 * room). The model and each of its parts are placed by placeInThree and
 * repeated by their copies; the model's copies carry its parts with them.
 * The stand-in's meshes are hidden once the model itself is drawn, and
 * `onReady` asks for a frame. Previews that fail to load leave the stand-in.
 */
function showPreview(group: THREE.Group, frame: THREE.Object3D, preview: PropPreview, onReady?: () => void): void {
  if (!preview.model.url) return;
  const standIns: THREE.Object3D[] = [];
  group.traverse((o) => { if ((o as THREE.Mesh).isMesh) { o.userData.standIn = true; standIns.push(o); } });
  const holder = new THREE.Group();
  holder.name = 'prop-preview';
  holder.userData.propPreview = true;
  frame.add(holder);

  const modelMoves = copyOffsetsFeet(preview.model.modelBounds, preview.model.transform);
  const place = (file: THREE.Group, view: PropModelView, extraMoves: [number, number, number][]) => {
    const p = placeInThree({ modelBounds: view.modelBounds, transform: view.transform });
    const ownMoves = copyOffsetsFeet(view.modelBounds, view.transform);
    for (const m of extraMoves) {
      for (const own of ownMoves) {
        const copy = previewInstance(file);
        copy.position.set(p.position[0] + m[0] + own[0], p.position[1] + m[1] + own[1], p.position[2] + m[2] + own[2]);
        copy.quaternion.set(p.quaternion[0], p.quaternion[1], p.quaternion[2], p.quaternion[3]);
        copy.scale.set(p.scale[0], p.scale[1], p.scale[2]);
        holder.add(copy);
      }
    }
  };

  loadPreviewFile(preview.model.url).then((file) => {
    if (!inScene(holder)) return;
    place(file, preview.model, [[0, 0, 0]]);
    for (const s of standIns) s.visible = false;
    onReady?.();
  }).catch(() => undefined);
  for (const part of preview.parts) {
    if (!part.url) continue;
    loadPreviewFile(part.url).then((file) => {
      if (!inScene(holder)) return;
      place(file, part, modelMoves);
      onReady?.();
    }).catch(() => undefined);
  }
}

/**
 * For the room export: the previews hidden and every stand-in shown, so the
 * file carries the stand-ins the renderer swaps for its own models (and none
 * of the previews' megabytes). Returns the undo.
 */
export function standInsForExport(roots: THREE.Object3D[]): () => void {
  const flipped: { object: THREE.Object3D; visible: boolean }[] = [];
  for (const root of roots) {
    root.traverse((o) => {
      if (o.userData.propPreview && o.visible) { flipped.push({ object: o, visible: true }); o.visible = false; }
      else if (o.userData.standIn && !o.visible) { flipped.push({ object: o, visible: false }); o.visible = true; }
    });
  }
  return () => { for (const f of flipped) f.object.visible = f.visible; };
}

/** Free a prop group's geometry and materials (the walls' own disposal covers hung props). A preview's geometry is shared with its file and stays. */
export function disposeStagedProps(group: THREE.Group | null): void {
  if (!group) return;
  group.traverse((c) => {
    const mesh = c as THREE.Mesh;
    if (!mesh.isMesh) return;
    if (!mesh.userData.propPreviewMesh) mesh.geometry.dispose();
    const m = mesh.material;
    (Array.isArray(m) ? m : [m]).forEach((x) => x.dispose());
  });
  group.removeFromParent();
}
